import React, { useState, useMemo, useCallback } from 'react';
import { Button, Tabs, Tag, Table, Input, Select, DatePicker, Modal, Descriptions, message } from 'antd';
import { DownloadOutlined, SendOutlined, SyncOutlined, ReloadOutlined, EyeOutlined, InfoCircleOutlined, StopOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  gerarProcuracao,
  registrarProcuracaoAvulsa,
  listarProcuracoesAvulsas,
  enviarProcuracaoAutentique,
  syncProcuracaoAutentique,
  cancelarProcuracaoAutentique,
  visualizarDocumentoProcuracao,
  type DadosManuaisProcuracao,
  type ProcuracaoAvulsa,
} from '../api/homologacao';

const UF_OPTIONS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const ESTADO_CIVIL_OPTIONS = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'];

const ehPJ = (tipo?: string) => tipo === 'PJ' || (tipo || '').toLowerCase().includes('jur');

const STATUS_COLOR: Record<string, string> = {
  Aguardando: 'orange',
  Assinado: 'green',
  Recusado: 'red',
  Gerada: 'default',
};

// Status efetivo: sem doc_id de assinatura → apenas "Gerada".
const statusEfetivo = (r: ProcuracaoAvulsa): string => r.status || (r.doc_id ? 'Aguardando' : 'Gerada');

const fmtData = (d: string | null) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—');

const DELIVERY_LABEL: Record<string, string> = { email: 'E-mail', whatsapp: 'WhatsApp', sms: 'SMS' };

interface SignerForm {
  name: string;
  email: string;
  phone: string;
  deliveryMethod: string;
  message: string;
}

export default function ProcuracaoPage() {
  const [dados, setDados] = useState<DadosManuaisProcuracao>({
    nomeResponsavel: '', cpfResponsavel: '', estadoCivil: '', profissao: '', tipoCliente: 'PF',
    logradouroInstalacao: '', numeroInstalacao: '', complementoInstalacao: '', bairroInstalacao: '',
    cidadeInstalacao: '', ufInstalacao: '', cepInstalacao: '',
  });
  const [gerando, setGerando] = useState(false);

  const [signer, setSigner] = useState<SignerForm>({
    name: '', email: '', phone: '', deliveryMethod: 'email',
    message: 'Prezado(a), segue a procuração para assinatura digital.',
  });
  const [homologacaoId, setHomologacaoId] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [autStatus, setAutStatus] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  // Aba de listagem
  const [lista, setLista] = useState<ProcuracaoAvulsa[]>([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [syncId, setSyncId] = useState<number | null>(null);
  const [busca, setBusca] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<string>('');
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // Envio a partir de uma linha da listagem
  const [enviarRow, setEnviarRow] = useState<ProcuracaoAvulsa | null>(null);
  const [enviandoRow, setEnviandoRow] = useState(false);
  const [rowSigner, setRowSigner] = useState<SignerForm>({
    name: '', email: '', phone: '', deliveryMethod: 'email',
    message: 'Prezado(a), segue a procuração para assinatura digital.',
  });

  // Visualizar documento / detalhes / cancelar a partir da listagem
  const [verId, setVerId] = useState<number | null>(null);
  const [detalhesRow, setDetalhesRow] = useState<ProcuracaoAvulsa | null>(null);
  const [cancelandoId, setCancelandoId] = useState<number | null>(null);

  const pj = ehPJ(dados.tipoCliente);

  const set = (k: keyof DadosManuaisProcuracao) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setDados((d) => ({ ...d, [k]: k === 'potenciaProjeto' ? Number(e.target.value) || undefined : e.target.value }));

  const field = (label: string, k: keyof DadosManuaisProcuracao, props?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input value={(dados[k] as string | number | undefined) ?? ''} onChange={set(k)} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" {...props} />
    </div>
  );

  const validarDados = (): boolean => {
    if (!dados.nomeResponsavel.trim()) { message.warning('Informe o nome completo.'); return false; }
    if (pj && !dados.razaoSocial?.trim()) { message.warning('Informe a Razão Social.'); return false; }
    return true;
  };

  // Cria/atualiza (merge) o registro avulso e devolve o id, reusando o da sessão.
  const garantirHomologacao = useCallback(async (): Promise<number> => {
    const { id } = await registrarProcuracaoAvulsa(dados, homologacaoId ?? undefined);
    if (!homologacaoId) setHomologacaoId(id);
    return id;
  }, [dados, homologacaoId]);

  const carregarLista = useCallback(async () => {
    setLoadingLista(true);
    try {
      setLista(await listarProcuracoesAvulsas());
    } catch (err) {
      console.error(err);
      message.error('Erro ao carregar procurações.');
    } finally {
      setLoadingLista(false);
    }
  }, []);

  const handleGerar = async () => {
    if (!validarDados()) return;
    setGerando(true);
    try {
      const blob = await gerarProcuracao({ dadosManuais: dados });
      await garantirHomologacao(); // registra na listagem
      const nomeArq = (pj ? dados.razaoSocial : dados.nomeResponsavel) || 'cliente';
      const isPdf = blob.type === 'application/pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Procuracao_${nomeArq.replace(/\s+/g, '_')}.${isPdf ? 'pdf' : 'docx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success('Procuração gerada.');
    } catch (err) {
      console.error(err);
      message.error('Erro ao gerar a procuração.');
    } finally {
      setGerando(false);
    }
  };

  const handleEnviar = async () => {
    if (!validarDados()) return;
    if (!signer.name.trim()) { message.warning('Informe o nome do signatário.'); return; }
    if (signer.deliveryMethod === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signer.email)) {
      message.warning('Informe um e-mail válido.'); return;
    }
    if (signer.deliveryMethod === 'whatsapp' && !signer.phone.trim()) {
      message.warning('Informe o WhatsApp.'); return;
    }
    setEnviando(true);
    try {
      const id = await garantirHomologacao();
      await enviarProcuracaoAutentique({
        dadosManuais: dados,
        homologacaoId: id,
        signerName: signer.name,
        signerEmail: signer.email || undefined,
        signerPhone: signer.phone || undefined,
        deliveryMethod: signer.deliveryMethod,
        message: signer.message,
        forcar: !!autStatus,
      });
      setAutStatus('Aguardando');
      setSignedUrl(null);
      message.success('Procuração enviada para assinatura.');
    } catch (err) {
      console.error(err);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg || 'Erro ao enviar para assinatura.');
    } finally {
      setEnviando(false);
    }
  };

  const handleSync = async () => {
    if (!homologacaoId) return;
    setSincronizando(true);
    try {
      const res = await syncProcuracaoAutentique(homologacaoId);
      if (res?.status) setAutStatus(res.status);
      if (res?.signedUrl) setSignedUrl(res.signedUrl);
      message.success('Status atualizado.');
    } catch (err) {
      console.error(err);
      message.error('Erro ao atualizar status.');
    } finally {
      setSincronizando(false);
    }
  };

  const handleSyncRow = async (id: number) => {
    setSyncId(id);
    try {
      await syncProcuracaoAutentique(id);
      await carregarLista();
    } catch (err) {
      console.error(err);
      message.error('Erro ao atualizar status.');
    } finally {
      setSyncId(null);
    }
  };

  const abrirEnviarRow = (row: ProcuracaoAvulsa) => {
    setDetalhesRow(null);
    setEnviarRow(row);
    // Reenvio: prefill com o contato usado no último envio.
    setRowSigner({
      name: row.signer_name || row.nome || '',
      email: row.signer_email || '',
      phone: row.signer_phone || '',
      deliveryMethod: row.delivery_method || 'email',
      message: 'Prezado(a), segue a procuração para assinatura digital.',
    });
  };

  // Abre uma URL/Blob de PDF numa nova aba.
  const abrirPdf = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // Abre o documento da listagem. Anexos manuais têm link local direto; o resto passa
  // pelo backend (busca a versão fresca no Autentique e, se falhar, regenera do snapshot).
  const handleVer = async (row: ProcuracaoAvulsa) => {
    if (row.signed_url && row.signed_url.startsWith('/')) { window.open(row.signed_url, '_blank', 'noopener'); return; }
    setVerId(row.id);
    try {
      abrirPdf(await visualizarDocumentoProcuracao(row.id));
    } catch (err) {
      console.error(err);
      message.error('Erro ao abrir o documento.');
    } finally {
      setVerId(null);
    }
  };

  // Pré-visualiza o PDF a partir dos dados atuais do formulário, antes de enviar.
  const handlePreviewForm = async () => {
    if (!validarDados()) return;
    setPreviewing(true);
    try {
      abrirPdf(await gerarProcuracao({ dadosManuais: dados }));
    } catch (err) {
      console.error(err);
      message.error('Erro ao gerar a pré-visualização.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCancelarRow = (row: ProcuracaoAvulsa) => {
    Modal.confirm({
      title: 'Cancelar procuração?',
      content: 'A solicitação de assinatura será cancelada no Autentique. Você poderá reenviá-la depois.',
      okText: 'Cancelar assinatura',
      okButtonProps: { danger: true },
      cancelText: 'Voltar',
      onOk: async () => {
        setCancelandoId(row.id);
        try {
          await cancelarProcuracaoAutentique(row.id);
          message.success('Procuração cancelada.');
          setDetalhesRow(null);
          await carregarLista();
        } catch (err) {
          console.error(err);
          message.error('Erro ao cancelar a procuração.');
        } finally {
          setCancelandoId(null);
        }
      },
    });
  };

  const handleEnviarRow = async () => {
    if (!enviarRow) return;
    if (!rowSigner.name.trim()) { message.warning('Informe o nome do signatário.'); return; }
    if (rowSigner.deliveryMethod === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rowSigner.email)) {
      message.warning('Informe um e-mail válido.'); return;
    }
    if (rowSigner.deliveryMethod === 'whatsapp' && !rowSigner.phone.trim()) {
      message.warning('Informe o WhatsApp.'); return;
    }
    setEnviandoRow(true);
    try {
      // Sem dadosManuais: o backend reusa o snapshot já salvo no registro.
      await enviarProcuracaoAutentique({
        homologacaoId: enviarRow.id,
        signerName: rowSigner.name,
        signerEmail: rowSigner.email || undefined,
        signerPhone: rowSigner.phone || undefined,
        deliveryMethod: rowSigner.deliveryMethod,
        message: rowSigner.message,
        // Já existe doc no Autentique (Aguardando/Recusado) → arquiva o anterior e reenvia.
        forcar: !!enviarRow.doc_id,
      });
      message.success('Procuração enviada para assinatura.');
      setEnviarRow(null);
      await carregarLista();
    } catch (err) {
      console.error(err);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg || 'Erro ao enviar para assinatura.');
    } finally {
      setEnviandoRow(false);
    }
  };

  const listaFiltrada = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const [de, ate] = periodo || [null, null];
    return lista.filter((r) => {
      if (q && !(r.nome || '').toLowerCase().includes(q)) return false;
      if (statusFiltro && statusEfetivo(r) !== statusFiltro) return false;
      if (de && dayjs(r.created_at).isBefore(de.startOf('day'))) return false;
      if (ate && dayjs(r.created_at).isAfter(ate.endOf('day'))) return false;
      return true;
    });
  }, [lista, busca, statusFiltro, periodo]);

  const colunas = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome', render: (v: string | null) => v || '—' },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 70, render: (v: string | null) => v || '—' },
    {
      title: 'Status', key: 'status', width: 120,
      render: (_: unknown, r: ProcuracaoAvulsa) => {
        const s = statusEfetivo(r);
        return <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>;
      },
    },
    { title: 'Criada em', dataIndex: 'created_at', key: 'created_at', width: 140, render: fmtData },
    { title: 'Enviada em', dataIndex: 'enviado_em', key: 'enviado_em', width: 140, render: fmtData },
    { title: 'Assinada em', dataIndex: 'assinado_em', key: 'assinado_em', width: 140, render: fmtData },
    {
      title: 'Enviado via', key: 'enviado_via', width: 120,
      render: (_: unknown, r: ProcuracaoAvulsa) =>
        r.delivery_method
          ? <span className="text-xs text-gray-600">{DELIVERY_LABEL[r.delivery_method] || r.delivery_method}</span>
          : <span className="text-xs text-gray-300">—</span>,
    },
    {
      title: 'Ações', key: 'acoes', width: 240,
      render: (_: unknown, r: ProcuracaoAvulsa) => {
        const s = statusEfetivo(r);
        return (
          <div className="flex gap-2 flex-wrap">
            <Button size="small" icon={<EyeOutlined />} loading={verId === r.id} onClick={() => handleVer(r)}>Ver</Button>
            {s !== 'Assinado' && (
              <Button size="small" type="primary" icon={<SendOutlined />} onClick={() => abrirEnviarRow(r)}>
                {s === 'Gerada' ? 'Enviar' : 'Reenviar'}
              </Button>
            )}
            {r.doc_id && (
              <Button size="small" icon={<SyncOutlined spin={syncId === r.id} />} loading={syncId === r.id} onClick={() => handleSyncRow(r.id)}>Status</Button>
            )}
            <Button size="small" icon={<InfoCircleOutlined />} onClick={() => setDetalhesRow(r)}>Detalhes</Button>
          </div>
        );
      },
    },
  ];

  const formDados = (
    <>
      <div className="mb-2">
        <label className="block text-xs font-medium text-gray-500 mb-1">Tipo de Cliente</label>
        <select value={dados.tipoCliente || 'PF'} onChange={set('tipoCliente') as (e: React.ChangeEvent<HTMLSelectElement>) => void} className="w-full sm:w-1/2 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
          <option value="PF">Pessoa Física (PF)</option>
          <option value="PJ">Pessoa Jurídica (PJ)</option>
        </select>
      </div>

      {pj && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-3">
          <p className="text-xs font-medium text-gray-600 mb-2">Dados da Empresa</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            {field('Razão Social *', 'razaoSocial')}
            {field('CNPJ *', 'cnpjEmpresa', { placeholder: '00.000.000/0000-00' })}
            {field('Tipo PJ (Ltda, S.A., MEI...)', 'tipoPessoaJuridica')}
            {field('Finalidade da Empresa', 'finalidadeEmpresa')}
          </div>
        </div>
      )}

      <p className="text-xs font-medium text-gray-600 mb-2">{pj ? 'Responsável Legal' : 'Titular'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        {field('Nome completo *', 'nomeResponsavel')}
        {field('CPF', 'cpfResponsavel', { placeholder: '000.000.000-00' })}
        {pj && field('Cargo', 'cargoResponsavel')}
        {field('E-mail', 'email', { type: 'email', placeholder: 'email@exemplo.com' })}
        {field('Telefone', 'telefone', { placeholder: '(00) 00000-0000' })}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Estado Civil</label>
          <select value={dados.estadoCivil || ''} onChange={set('estadoCivil') as (e: React.ChangeEvent<HTMLSelectElement>) => void} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
            <option value="">Selecione</option>
            {ESTADO_CIVIL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        {field('Profissão', 'profissao')}
      </div>

      <p className="text-xs font-medium text-gray-600 mb-2 mt-2">Endereço {pj ? 'da Empresa' : 'de Instalação'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        {field('Logradouro', 'logradouroInstalacao')}
        {field('Número', 'numeroInstalacao')}
        {field('Complemento', 'complementoInstalacao')}
        {field('Bairro', 'bairroInstalacao')}
        {field('Cidade', 'cidadeInstalacao')}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">UF</label>
          <select value={dados.ufInstalacao || ''} onChange={set('ufInstalacao') as (e: React.ChangeEvent<HTMLSelectElement>) => void} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
            <option value="">Selecione</option>
            {UF_OPTIONS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
          </select>
        </div>
        {field('CEP', 'cepInstalacao', { placeholder: '00000-000' })}
      </div>

      {!pj && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          {field('Potência do Projeto (kWp)', 'potenciaProjeto', { type: 'number', placeholder: '≤60 Marcos, senão Geraldo' })}
        </div>
      )}
    </>
  );

  const abaDownload = (
    <div className="flex flex-wrap gap-2 pt-1">
      <Button type="primary" icon={<DownloadOutlined />} loading={gerando} onClick={handleGerar}>Baixar Procuração</Button>
    </div>
  );

  const abaAssinatura = (
    <div className="pt-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Nome do signatário *</label>
          <input value={signer.name} onChange={(e) => setSigner({ ...signer, name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
        </div>
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Método de envio</label>
          <select value={signer.deliveryMethod} onChange={(e) => setSigner({ ...signer, deliveryMethod: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
            <option value="email">E-mail</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </div>
        {signer.deliveryMethod === 'email' ? (
          <div className="mb-3 sm:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">E-mail *</label>
            <input value={signer.email} onChange={(e) => setSigner({ ...signer, email: e.target.value })} type="email" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
          </div>
        ) : (
          <div className="mb-3 sm:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">WhatsApp *</label>
            <input value={signer.phone} onChange={(e) => setSigner({ ...signer, phone: e.target.value })} placeholder="(00) 00000-0000" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
          </div>
        )}
        <div className="mb-3 sm:col-span-2">
          <label className="block text-xs font-medium text-gray-500 mb-1">Mensagem</label>
          <textarea value={signer.message} onChange={(e) => setSigner({ ...signer, message: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
        </div>
      </div>

      {autStatus && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-3 flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm text-gray-600">
            Status: <Tag color={STATUS_COLOR[autStatus] || 'default'}>{autStatus}</Tag>
            {signedUrl && <a href={signedUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 ml-2">ver documento assinado</a>}
          </span>
          <Button size="small" icon={<SyncOutlined spin={sincronizando} />} loading={sincronizando} onClick={handleSync}>Atualizar status</Button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button icon={<EyeOutlined />} loading={previewing} onClick={handlePreviewForm}>Pré-visualizar</Button>
        <Button type="primary" icon={<SendOutlined />} loading={enviando} onClick={handleEnviar}>
          {autStatus ? 'Reenviar para Assinatura' : 'Enviar para Assinatura'}
        </Button>
      </div>
    </div>
  );

  const abaLista = (
    <div className="pt-1">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input.Search allowClear placeholder="Buscar por nome" value={busca} onChange={(e) => setBusca(e.target.value)} style={{ width: 220 }} />
        <Select
          allowClear placeholder="Status" value={statusFiltro || undefined} onChange={(v) => setStatusFiltro(v || '')} style={{ width: 150 }}
          options={[
            { value: 'Gerada', label: 'Gerada' },
            { value: 'Aguardando', label: 'Aguardando' },
            { value: 'Assinado', label: 'Assinado' },
            { value: 'Recusado', label: 'Recusado' },
          ]}
        />
        <DatePicker.RangePicker format="DD/MM/YYYY" value={periodo as never} onChange={(v) => setPeriodo(v as [Dayjs | null, Dayjs | null] | null)} />
        <Button icon={<ReloadOutlined />} loading={loadingLista} onClick={carregarLista}>Atualizar</Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loadingLista}
        dataSource={listaFiltrada}
        columns={colunas}
        scroll={{ x: 800 }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
      />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: '24px 16px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', background: '#fff', borderRadius: 12, border: '1px solid #eee', padding: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 4px' }}>Gerar Procuração</h2>
        <p className="text-xs text-gray-400 mb-5">Preencha os dados e baixe o documento ou envie para assinatura digital. Esta página não vincula a procuração a nenhum cliente.</p>

        <Tabs
          defaultActiveKey="gerar"
          onChange={(k) => { if (k === 'lista') carregarLista(); }}
          items={[
            {
              key: 'gerar',
              label: 'Gerar / Enviar',
              children: (
                <>
                  {formDados}
                  <div className="border-t border-gray-100 mt-4 pt-2">
                    <Tabs
                      items={[
                        { key: 'download', label: 'Baixar PDF', children: abaDownload },
                        { key: 'assinatura', label: 'Enviar para Assinatura', children: abaAssinatura },
                      ]}
                    />
                  </div>
                </>
              ),
            },
            { key: 'lista', label: 'Procurações', children: abaLista },
          ]}
        />
      </div>

      <Modal
        title={`Enviar para assinatura${enviarRow?.nome ? ` — ${enviarRow.nome}` : ''}`}
        open={!!enviarRow}
        onCancel={() => setEnviarRow(null)}
        onOk={handleEnviarRow}
        okText="Enviar"
        confirmLoading={enviandoRow}
      >
        <div className="pt-2">
          <Button size="small" icon={<EyeOutlined />} loading={verId === enviarRow?.id} onClick={() => enviarRow && handleVer(enviarRow)}>
            Pré-visualizar documento
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 pt-3">
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">Nome do signatário *</label>
            <input value={rowSigner.name} onChange={(e) => setRowSigner({ ...rowSigner, name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">Método de envio</label>
            <select value={rowSigner.deliveryMethod} onChange={(e) => setRowSigner({ ...rowSigner, deliveryMethod: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black">
              <option value="email">E-mail</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          {rowSigner.deliveryMethod === 'email' ? (
            <div className="mb-3 sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">E-mail *</label>
              <input value={rowSigner.email} onChange={(e) => setRowSigner({ ...rowSigner, email: e.target.value })} type="email" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
            </div>
          ) : (
            <div className="mb-3 sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">WhatsApp *</label>
              <input value={rowSigner.phone} onChange={(e) => setRowSigner({ ...rowSigner, phone: e.target.value })} placeholder="(00) 00000-0000" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
            </div>
          )}
          <div className="mb-1 sm:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Mensagem</label>
            <textarea value={rowSigner.message} onChange={(e) => setRowSigner({ ...rowSigner, message: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-black" />
          </div>
        </div>
      </Modal>

      <Modal
        title={`Detalhes${detalhesRow?.nome ? ` — ${detalhesRow.nome}` : ''}`}
        open={!!detalhesRow}
        onCancel={() => setDetalhesRow(null)}
        footer={null}
      >
        {detalhesRow && (() => {
          const s = statusEfetivo(detalhesRow);
          const contato = detalhesRow.delivery_method === 'email'
            ? detalhesRow.signer_email
            : detalhesRow.signer_phone;
          return (
            <div className="pt-2">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Tipo">{detalhesRow.tipo || '—'}</Descriptions.Item>
                <Descriptions.Item label="Status"><Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag></Descriptions.Item>
                <Descriptions.Item label="Signatário">{detalhesRow.signer_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Enviado via">
                  {detalhesRow.delivery_method ? (DELIVERY_LABEL[detalhesRow.delivery_method] || detalhesRow.delivery_method) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Contato">{contato || '—'}</Descriptions.Item>
                <Descriptions.Item label="Criada em">{fmtData(detalhesRow.created_at)}</Descriptions.Item>
                <Descriptions.Item label="Enviada em">{fmtData(detalhesRow.enviado_em)}</Descriptions.Item>
                <Descriptions.Item label="Assinada em">{fmtData(detalhesRow.assinado_em)}</Descriptions.Item>
              </Descriptions>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button icon={<EyeOutlined />} loading={verId === detalhesRow.id} onClick={() => handleVer(detalhesRow)}>
                  Ver documento
                </Button>
                {s !== 'Assinado' && (
                  <Button type="primary" icon={<SendOutlined />} onClick={() => abrirEnviarRow(detalhesRow)}>
                    {s === 'Gerada' ? 'Enviar' : 'Reenviar'}
                  </Button>
                )}
                {detalhesRow.doc_id && s !== 'Assinado' && (
                  <Button danger icon={<StopOutlined />} loading={cancelandoId === detalhesRow.id} onClick={() => handleCancelarRow(detalhesRow)}>
                    Cancelar assinatura
                  </Button>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
